import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Collapse, Table } from 'antd'
import { filter, find, isEmpty, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import { StyledFullWidthTableContainer } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { flattenTreeStructure } from '../../../common/utils'

import { useUserHasPermissions } from '../../../account/hooks'
import { labelLegendPropType } from '../../../menu-curation-tasks/prop-types'
import { computeMenuTitleHighlights } from '../../helpers'
import { menuTitlePropType } from '../../prop-types'
import {
  setMenuTitleExpandedRowKeys,
  setMenuTitleForIgnoredDishType,
  setMenuTitleHighlightedRowKey,
} from '../../redux/actions'
import { createMenuTitleTableColumns } from '../../services'
import { transformMenuTitlesToTableView } from '../../transformers'
import StyledMenuTitleCollapsePanel from '../styled/styled-menu-title-collapse-panel'

import MenuTitleEditableRow from './menu-title-editable-row'

const MenuTitlesTable = ({
  menuTitles,
  dietLabelLegends,
  allergenLabelLegends,
  isDisabled,
  isQaCorrected,
}) => {
  const dispatch = useDispatch()
  const [isTableCollapsed, setIsTableCollapsed] = useState(false)

  const {
    menuTitleExpandedRowKeys: expandedRowKeys,
    menuTitleHighlightedRowKey: highlightedRowKey,
    data: menuCurationTask,
  } = useSelector(store => store.menuCurationTasks.menuCurationTask)

  const hasUpdatePermissions = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UPDATE_MENU_TITLE,
  )
  const hasSetIgnoredPermissions = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.SET_MENU_TITLE_IGNORED,
  )

  const highlights = useMemo(
    () => computeMenuTitleHighlights(dietLabelLegends, allergenLabelLegends),
    [allergenLabelLegends, dietLabelLegends],
  )

  const handleTabelCollapse = useCallback(() => {
    setIsTableCollapsed(!isTableCollapsed)
  }, [isTableCollapsed])

  const rowKeyFunction = useCallback(({ id }) => `menuTitle-${id}`, [])

  const rowClassNameFunction = useCallback(
    ({ id }) => {
      if (`menuTitle-${id}` === highlightedRowKey) {
        return 'menuTitleRow-legend-highlight'
      }

      return null
    },
    [highlightedRowKey],
  )

  const handleRowExpansion = useCallback(
    (expanded, { id }) =>
      dispatch(
        setMenuTitleExpandedRowKeys(
          expanded
            ? [...(expandedRowKeys || []), `menuTitle-${id}`]
            : filter(expandedRowKeys, key => key !== `menuTitle-${id}`),
        ),
      ),
    [dispatch, expandedRowKeys],
  )

  const menuTitlesForDisplay = useMemo(
    () => transformMenuTitlesToTableView(menuTitles),
    [menuTitles],
  )

  const menuTitlesFlat = useMemo(
    () => flattenTreeStructure(menuTitles),
    [menuTitles],
  )

  const handleSetMenuTitleForIgnoredDishType = useCallback(
    menuTitleId => {
      const menuTitle = find(menuTitlesFlat, menuTitle =>
        areSameValue(menuTitle.id, menuTitleId),
      )

      dispatch(setMenuTitleForIgnoredDishType({ menuTitle }))
    },
    [dispatch, menuTitlesFlat],
  )

  const columns = useMemo(
    () =>
      createMenuTitleTableColumns({
        handleSetMenuTitleForIgnoredDishType,
        isDescriptionDisabled: isDisabled || !hasUpdatePermissions,
        isIgnoreButtonDisabled: isDisabled || !hasSetIgnoredPermissions,
        menuTitlesFlat,
        highlights,
        isMenuFake: menuCurationTask.menu.isFake,
      }),
    [
      handleSetMenuTitleForIgnoredDishType,
      hasSetIgnoredPermissions,
      hasUpdatePermissions,
      highlights,
      isDisabled,
      menuCurationTask.menu.isFake,
      menuTitlesFlat,
    ],
  )

  const tableComponents = useMemo(
    () => ({
      body: {
        row: MenuTitleEditableRow,
      },
    }),
    [],
  )

  useEffect(() => {
    if (!isNil(highlightedRowKey)) {
      const selectedRow = document.querySelector(
        `[data-row-key="${highlightedRowKey}"]`,
      )

      if (!isEmpty(selectedRow)) {
        selectedRow.scrollIntoView({ block: 'center' })
      }
    }
  }, [highlightedRowKey])

  useEffect(() => {
    let highlightTimeout
    if (!isNil(highlightedRowKey)) {
      setIsTableCollapsed(false)
      highlightTimeout = setTimeout(
        () => dispatch(setMenuTitleHighlightedRowKey(null)),
        5000,
      )
    }

    return () => clearTimeout(highlightTimeout)
  }, [dispatch, highlightedRowKey])

  return (
    <StyledFullWidthTableContainer>
      <Collapse
        activeKey={!isTableCollapsed ? 'table' : null}
        onChange={handleTabelCollapse}
      >
        <StyledMenuTitleCollapsePanel header="Menu Titles" key="table">
          <Table
            className={`menu-title-table ${
              isQaCorrected ? 'highlight-red' : ''
            }`}
            components={tableComponents}
            pagination={false}
            dataSource={menuTitlesForDisplay}
            columns={columns}
            rowClassName={rowClassNameFunction}
            rowKey={rowKeyFunction}
            MenuTitleEditableRow
            expandedRowKeys={expandedRowKeys}
            onExpand={handleRowExpansion}
          />
        </StyledMenuTitleCollapsePanel>
      </Collapse>
    </StyledFullWidthTableContainer>
  )
}

MenuTitlesTable.propTypes = {
  menuTitles: PropTypes.arrayOf(menuTitlePropType),
  dietLabelLegends: PropTypes.arrayOf(labelLegendPropType),
  allergenLabelLegends: PropTypes.arrayOf(labelLegendPropType),
  isDisabled: PropTypes.bool,
  isQaCorrected: PropTypes.bool,
}

MenuTitlesTable.defaultProps = {
  dietLabelLegends: [],
  allergenLabelLegends: [],
  menuTitles: [],
  isDisabled: false,
  isQaCorrected: false,
}

export default MenuTitlesTable
